import { debounce, isEmpty, isNumber } from 'lodash';
import { ChangeEvent, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import CommonTooltip from '~/components/CommonTooltip';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import useHostingInfo from '~/hooks/useHostingInfo';
import useHostingNavigate from '~/hooks/useHostingNavigate';
import BtnGroup from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/BtnGroup';
import CardHeader from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/CardHeader';
import DataCenter from '~/scenes/Cloud/components/DataCenter';
import Storage from '~/scenes/Cloud/components/Storage';
import UpsellResourcesBanner from '~/scenes/Cloud/components/UpsellResourcesBanner';
import Workers from '~/scenes/Cloud/components/Workers';
import { getMultisiteNetworkConfigOptions } from '~/scenes/Cloud/utils/helpers';
import { validateStorage, validateWorkers } from '~/utils/cloud/validations';
import { validateEmail } from '~/utils/email/validations';
import {
  calculateMaxStorageAllowed,
  getSiteStorageQuotaLimit,
} from '~/utils/hosting/helpers';

import { defaultFormData, FormDataType } from '../..';
import MultisiteInfo from './components/MultisiteInfo';
import MultisiteOption from './components/MultisiteOption';

export type ChooseTitlePropOptions = {
  formData: FormDataType;
  changeLoading: boolean;
};

const ChooseTitle = ({ formData, changeLoading }: ChooseTitlePropOptions) => {
  const { t } = useTranslation('addSites');
  const { t: tCloud } = useTranslation('cloud');
  const { multisiteLearnMoreUrl } = useBrandInfo();
  const navigate = useNavigate();
  const hostingNavigate = useHostingNavigate();
  const { register, watch, reset, setValue } = useFormContext();

  const siteTitle = watch('title');
  const adminEmail = watch('admin_email');
  const adminUser = watch('admin_user');
  // Multisite type will be either 'subdomain_multisite' or 'subdirectory_multisite'
  const isMultisiteEnabled = watch('enable_multisite');
  const multisiteType = watch('multisite_type');

  const titleIsEmptyByDefault = formData?.title === '';
  const titleIsEmpty = siteTitle === '';
  const titleIsValidByDefault =
    titleIsEmptyByDefault || formData?.title?.trim() !== '';

  const emailIsEmptyByDefault = formData?.admin_email === '';
  const emailIsEmpty = adminEmail === '';
  const emailIsValidByDefault =
    emailIsEmptyByDefault || validateEmail(formData?.admin_email);

  const usernameIsEmptyByDefault = formData?.admin_user === '';
  const usernameIsEmpty = adminUser === '';
  const usernameIsValidByDefault =
    usernameIsEmptyByDefault || formData?.admin_user?.trim() !== '';

  const [titleIsValid, setTitleIsValid] = useState(titleIsValidByDefault);
  const [emailIsValid, setEmailIsValid] = useState(emailIsValidByDefault);
  const [usernameIsValid, setUsernameIsValid] = useState(
    usernameIsValidByDefault,
  );
  const [isEditingForm, setIsEditingForm] = useState(false);

  const { hostingAccount, isPending } = useHostingInfo();
  const storageInfo = hostingAccount?.data?.resources?.storage;
  const workersInfo = hostingAccount?.data?.resources?.workers;
  const siteStorageQuota = getSiteStorageQuotaLimit(storageInfo);
  const isWpInstallType =
    formData?.action === 'add' &&
    formData?.subtype?.toLowerCase() === 'wordpress';
  const handleCancel = () => {
    reset(defaultFormData);
    hostingNavigate();
  };

  const addSite = () => {
    navigate('../connect');
  };

  const handleTitleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setValue('title', e.target.value);
    setTitleIsValid(e.target.value.trim() !== '');
  };

  const handleUsernameOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setValue('admin_user', e.target.value);
    setUsernameIsValid(e.target.value.trim() !== '');
  };

  const handleEmailValidation = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    e.preventDefault();
    const email = e.target.value;
    setValue('admin_email', email);
    const debounceValidation = debounce(() => {
      const isValid = validateEmail(email);
      setEmailIsValid(isValid);
      setIsEditingForm(false);
      if (isEmpty(adminUser) && isValid) {
        const username = email.split('@')[0];
        setValue('admin_user', username);
        setUsernameIsValid(e.target.value.trim() !== '');
      }
    }, 1000);
    debounceValidation();
  };

  const minStorageAllowed = 1;
  const maxStorageAllowed = calculateMaxStorageAllowed({
    storageAvailable: storageInfo?.available,
    siteStorageQuota,
  });

  const { isValid: storageIsValid, isExceedMaxStorage } = validateStorage({
    value: watch('disk_limit_gib'),
    min: minStorageAllowed,
    max: maxStorageAllowed,
    siteStorageQuota,
    t: tCloud,
  });

  const minWorkersAllowed = 2;
  const maxWorkersAllowed =
    Number(workersInfo?.available) <= 10 ? workersInfo?.available : 10;

  const { isValid: workersIsValid } = validateWorkers({
    value: watch('workers'),
    min: minWorkersAllowed,
    max: Number(maxWorkersAllowed),
    t: tCloud,
  });

  const handleNetworkConfigurationChange = (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = e.target.checked;
    setValue('enable_multisite', checked);
    if (checked && !watch('multisite_type')) {
      setValue('multisite_type', 'subdomain');
    }
  };

  const disabledContinueBtn =
    isPending ||
    titleIsEmpty ||
    !titleIsValid ||
    emailIsEmpty ||
    !emailIsValid ||
    usernameIsEmpty ||
    !usernameIsValid ||
    !watch('datacenter') ||
    !storageIsValid ||
    !workersIsValid ||
    isEditingForm;

  const MULTISITE_OPTIONS = getMultisiteNetworkConfigOptions(t);

  const handleMultisiteTypeChange = (key: string) => () => {
    setValue('multisite_type', key);
  };

  return (
    <Stack spacing={2}>
      <CardHeader headerTitle={t('chooseSiteTitle')} hasBackArrow={false} />
      <Typography>{t('websiteNameDescription')}</Typography>
      <Grid container flexDirection="column" rowSpacing={3}>
        <Grid item xs={12} md={10}>
          <TextField
            {...register('title')}
            fullWidth
            id="site-title"
            label={t('siteTitle.titleLabel')}
            data-testid="site-title-input"
            variant="outlined"
            value={siteTitle}
            error={!titleIsValid}
            helperText={!titleIsValid && 'field required'}
            onChange={handleTitleOnChange}
          />
        </Grid>
        <Grid item xs={12} md={10}>
          <TextField
            {...register('admin_email')}
            fullWidth
            id="admin-email"
            label={t('siteTitle.emailLabel')}
            data-testid="admin-email-input"
            variant="outlined"
            value={adminEmail}
            error={!emailIsValid}
            helperText={!emailIsValid && 'please enter a valid email address'}
            type="email"
            onChange={(e) => {
              setIsEditingForm(true);
              handleEmailValidation(e);
            }}
          />
        </Grid>
        <Grid item xs={12} md={10}>
          <TextField
            {...register('admin_user')}
            fullWidth
            id="admin-user"
            label={t('siteTitle.usernameLabel')}
            data-testid="admin-user-input"
            variant="outlined"
            value={adminUser}
            error={!usernameIsValid}
            helperText={!usernameIsValid && 'field required'}
            onChange={handleUsernameOnChange}
          />
        </Grid>
        <Grid item xs={12} md={10}>
          <Divider />
        </Grid>
        <Grid item xs={12} md={10}>
          {isExceedMaxStorage && (
            <UpsellResourcesBanner
              title={t('resourcesBanner.cloneTitle')}
              desc={t('resourcesBanner.cloneDesc', {
                storage: storageInfo?.available,
              })}
              isCloneWP={true}
            />
          )}
        </Grid>
        <Grid item xs={12} md={10}>
          <Typography variant="h3">{t('siteTitle.advancedHeader')}</Typography>
        </Grid>
        <Grid item xs={12} md={10}>
          <DataCenter showLabel defaultValue={formData.datacenter} />
        </Grid>
        <Grid item xs={12} md={10}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Stack spacing={1}>
              <Storage showLabel defaultValue={formData.disk_limit_gib} />
              {isNumber(storageInfo?.available) && (
                <Typography variant="h4">
                  {t('siteTitle.storageAvailable', {
                    amount: storageInfo?.available,
                    unit: storageInfo?.unit,
                  })}
                </Typography>
              )}
            </Stack>
            <Stack spacing={1}>
              <Workers showLabel defaultValue={formData.workers} />
              {isNumber(workersInfo?.available) && (
                <Typography variant="h4">
                  {t('siteTitle.phpWorkersAvailable', {
                    amount: workersInfo?.available,
                  })}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Grid>
        {isWpInstallType && (
          <Grid item xs={12} md={10}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="nowrap"
            >
              <Switch
                {...register('enable_multisite')}
                checked={isMultisiteEnabled}
                onChange={handleNetworkConfigurationChange}
                data-testid="enable-multisite-toggle"
                sx={{ ml: 0 }}
              />

              <Typography>
                <Trans>{t('multisiteInfo.enableMultisite')}</Trans>
              </Typography>
              <CommonTooltip
                title={t('multisiteInfo.enableMultisiteTooltip')}
                data-testid="enable-multisite-tooltip"
                placement="top-start"
              >
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ color: 'text.secondary', cursor: 'pointer' }}
                />
              </CommonTooltip>
            </Stack>
          </Grid>
        )}
        {isMultisiteEnabled && (
          <Grid item xs={12}>
            <Stack spacing={3}>
              <MultisiteInfo multisiteLearnMoreUrl={multisiteLearnMoreUrl} />
              <Stack spacing={1}>
                <Typography variant="h3">
                  {t('multisiteInfo.networkConfiguration')}
                </Typography>

                {MULTISITE_OPTIONS.map((option) => {
                  const isSelected = multisiteType === option.key;
                  return (
                    <MultisiteOption
                      key={option.key}
                      option={option}
                      isSelected={isSelected}
                      onSelect={handleMultisiteTypeChange(option.key)}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Grid>
        )}
        <Grid item>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <BtnGroup
              disabled={disabledContinueBtn}
              hasSkipBtn={false}
              hasCancelBtn
              onCancelHandler={handleCancel}
              onClickHandler={addSite}
              loading={changeLoading}
              dataTestId={'add-site-flow-btn'}
            />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ChooseTitle;
